import React from "react";
import {
  Box,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import { ResponsiveContainer } from "recharts";

export const PRIMARY = "#9D2362";
export const MAGENTA_TINT = "#F9E6F0";
export const CARD_BORDER = "1px solid #ececf0";
export const GREEN = "#2e7d32";
export const INDIGO = "#3F51B5";

/** Category / series palette, matching the legacy Ecosystem report. */
export const CATEGORY_COLORS = [
  PRIMARY,
  INDIGO,
  GREEN,
  "#fdb814",
  "#00838f",
  "#8e24aa",
  "#e64a19",
  "#5d4037",
  "#455a64",
  "#c2185b",
];

export const SectionCard: React.FC<{ children: React.ReactNode; sx?: any }> = ({
  children,
  sx,
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

export const SectionTitle: React.FC<{
  children: React.ReactNode;
  subtitle?: string;
}> = ({ children, subtitle }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

export const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  height: number | string;
  children: any;
  sx?: any;
}> = ({ title, subtitle, height, children, sx }) => (
  <SectionCard sx={{ flex: "1 1 380px", minWidth: 0, ...sx }}>
    <SectionTitle subtitle={subtitle}>{title}</SectionTitle>
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </SectionCard>
);

/** Completion-rate meter used in every grouping table. */
export const CompletionBar: React.FC<{ rate: number }> = ({ rate }) => {
  const pct = Math.round((rate || 0) * 100);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        justifyContent: "flex-end",
      }}
    >
      <Box sx={{ width: 64 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 7,
            borderRadius: 4,
            backgroundColor: "#eef0f2",
            "& .MuiLinearProgress-bar": {
              backgroundColor: GREEN,
              borderRadius: 4,
            },
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 36, textAlign: "right" }}>
        {pct}%
      </Typography>
    </Box>
  );
};

export const EmptyState: React.FC<{ title: string; hint?: string }> = ({
  title,
  hint,
}) => (
  <SectionCard sx={{ textAlign: "center", py: 8 }}>
    <Typography variant="h6" sx={{ color: "#8a8a93" }}>
      {title}
    </Typography>
    {hint ? (
      <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
        {hint}
      </Typography>
    ) : null}
  </SectionCard>
);

export const percentText = (rate: number) => `${Math.round((rate || 0) * 100)}%`;
